/**
 * Rules are documented here:
 * https://atlanticmedia.atlassian.net/wiki/spaces/PEG/pages/1908080665/Client+Side+Injector
 */

import {
  InjectedTypes,
  ArticleElementTypes,
  Rule,
  RuleInPixels,
} from "types/injector";
import log from "utils/csi/injectorLogger";
import { RECIRC_HEIGHT, BOOKS_MAX_INSET_HEIGHT } from "./injector";

const rulesFactory = (type: InjectedTypes, componentHeight: number): Rule[] => {
  return [
    // Injected Item must be 250px below any other injected item
    {
      item: type,
      after: "250px",
      from: InjectedTypes.Any,
    },
    // Injected Item cannot appear alongside hr
    {
      item: type,
      before: "1px",
      from: ArticleElementTypes.HorizRule,
    },
    // Injected Item cannot appear alongside dropcap paragraph
    {
      item: type,
      before: "1px",
      from: ArticleElementTypes.Dropcap,
    },
    // Injected items must be 100px away from LegacyHtml blocks
    {
      item: type,
      after: "100px",
      before: "100px",
      from: ArticleElementTypes.LegacyHtml,
    },
    // Injected items must be 250px after current type-to-be-injected
    {
      item: InjectedTypes.Any,
      after: componentHeight + 250 + "px",
      from: type,
    },
  ];
};

/**
 * Convert 100vh or 100px into a number of pixels.
 *
 * @param str
 * @returns
 */
function parseToPx(str: string): number | false {
  const viewportHeight = window.innerHeight;
  const unit = str.substr(-2);
  const value = str.substring(0, str.length - 2);
  if (unit == "px") {
    return parseInt(value, 10);
  } else if (unit == "vh") {
    return (parseFloat(value) / 100) * viewportHeight;
  }

  log.debug(`Couldn't parse unit for rule`, str);
  return false;
}

/**
 * Returns a list of rules to use in the injector
 * This is a function so we could programmatically change what it returns in the future.
 * For example, we could return a different list if isMobile or for an AB Test.
 */
export function getInjectionRules(
  isMobile: boolean,
  testVariant: string | null
): Rule[] {
  const rules: Rule[] = [
    // Injected items must be at least 150px below and 100px above a figure (image/video)
    {
      item: InjectedTypes.Any,
      after: "150px",
      before: "100px",
      from: ArticleElementTypes.Figure,
    },
    // Injected items cannot be adjacent to a blockquote or pullquote
    {
      item: InjectedTypes.Any,
      after: "1px",
      before: "1px",
      from: ArticleElementTypes.Quote,
    },
    // Injected items cannot be immediately after a heading
    {
      item: InjectedTypes.Any,
      after: "1px",
      from: ArticleElementTypes.Heading,
    },
    // Injected items cannot be immediately before a list
    {
      item: InjectedTypes.Any,
      before: "1px",
      from: ArticleElementTypes.List,
    },
    // Injected items cannot be immediately after a
    // paragraph that ends with a comma or colon
    {
      item: InjectedTypes.Any,
      after: "1px",
      from: ArticleElementTypes.Salutations,
    },
    // Injected ads must be about 1 browser height after the last injected ad
    // Set slightly below 100vh to avoid having a 3 pixel rounding error
    // push the ad down another position, but now lower than 700px to avoid
    // too much density on mobile.
    {
      item: InjectedTypes.Ad,
      after: `${Math.max(parseToPx("95vh") as number, 700)}px`,
      from: InjectedTypes.Ad,
    },
    // Injected ads must be 1 browser height away from manually placed ads
    {
      item: InjectedTypes.Ad,
      after: "100vh",
      before: "100vh",
      from: ArticleElementTypes.AdPlaceholder,
    },
    // Injected house ads (newsletters, magazine promos) must
    // be 1/2 a browser heist after injected ad.
    {
      item: InjectedTypes.Ad,
      after: "50vh",
      from: InjectedTypes.House,
    },
    // Any injected item should be at least 200px from the end of the article
    {
      item: InjectedTypes.Any,
      before: "200px",
      from: ArticleElementTypes.End,
    },
    // Injected items must be 250px after injected recirc
    {
      item: InjectedTypes.Any,
      after: RECIRC_HEIGHT + 250 + "px",
      from: InjectedTypes.Recirc,
    },
    // Any injected item must be at least 250px away from non-injected recirc units
    {
      item: InjectedTypes.Any,
      after: "250px",
      before: "250px",
      from: ArticleElementTypes.RecircPlaceholder,
    },
    // The first injected item cannot be immediately before a dropcap paragraph
    {
      item: InjectedTypes.Any,
      index: 0,
      before: "300px",
      from: ArticleElementTypes.Dropcap,
    },
    // There must be at least 320px between the top of the article the first injected item on mobile
    // and 200px on desktop
    {
      item: InjectedTypes.Any,
      index: 0,
      after: isMobile ? "320px" : "350px",
      from: ArticleElementTypes.Begin,
    },
  ];

  rules.push(
    ...rulesFactory(InjectedTypes.Books, BOOKS_MAX_INSET_HEIGHT),
    ...rulesFactory(InjectedTypes.Recirc, RECIRC_HEIGHT)
  );

  return rules;
}

/**
 * Converts an array of rules to use the actual number of pixels on screen that that rule uses.
 * For example, a rule with "100vh" will be converted to the exact number of pixels of viewport
 * height
 */
export function convertRulesToPx(rules: Rule[]): RuleInPixels[] {
  return rules.map((rule) => {
    const newRule: RuleInPixels = {
      ...rule,
      before: undefined,
      after: undefined,
    };

    if (rule.before) {
      const px = parseToPx(rule.before.trim());
      if (px) {
        newRule.before = px;
      }
    }

    if (rule.after) {
      const px = parseToPx(rule.after.trim());
      if (px) {
        newRule.after = px;
      }
    }

    return newRule;
  });
}
